import {
  Avatar,
  Box,
  Flex,
  Spinner,
  Text,
  Tooltip,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { Command } from 'cmdk';
import { AnimatePresence, motion } from 'framer-motion';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdDarkMode, MdOutlineAdd, MdSearch, MdPerson } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { canAccessAdminPath, canManageOrgResources } from 'routes/access-policy';
import { RType } from 'routes/types';
import { typography } from 'theme/tokens';
import { translate } from '../../i18n';
import { useUserRole } from '../../features/auth/roles';
import { useSelectedOrganization } from '../../features/organization/hooks/useSelectedOrganization';
import { ReadState, scopePromptI18nKey } from '../../features/_shared/readState';
import { useEntitySearch, UserSearchResult } from './useEntitySearch';
import { useRecentPages } from './useRecentPages';

interface CommandPaletteProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  visibleRoutes: RType[];
}

type SubCommand = 'users' | null;

export default function CommandPalette({ open, setOpen, visibleRoutes }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [subCommand, setSubCommand] = useState<SubCommand>(null);
  const [subQuery, setSubQuery] = useState('');
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const posthog = usePostHog();
  const { selectedOrganization } = useSelectedOrganization();
  const role = useUserRole();
  // The page list already comes pre-filtered via `visibleRoutes`; the quick
  // actions and the user search below navigate to hardcoded paths, so they
  // need their own check or they'd offer a coach a destination that just
  // bounces back to the calendar.
  const canSearchUsers = canAccessAdminPath(role, '/userDetails');
  const { recentPages, addRecentPage } = useRecentPages(visibleRoutes);
  const {
    results: userResults,
    isLoading: userSearchLoading,
    isError: userSearchError,
    hasQuery: hasUserQuery,
    state: userSearchState,
  } = useEntitySearch(selectedOrganization?.id, subQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Semantic colors
  const overlayBg = 'rgba(0,0,0,0.4)';
  const dialogBg = useColorModeValue('white', 'var(--chakra-colors-navy-800)');
  const borderColor = useColorModeValue('#E5E7EB', 'var(--chakra-colors-whiteAlpha-100)');
  const textPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textSecondary = useColorModeValue('secondaryGray.600', 'gray.400');
  const pageBg = useColorModeValue('secondaryGray.300', 'navy.900');
  const itemActiveBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const activeBorder = useColorModeValue('brand.500', 'brand.500');
  const shadow = useColorModeValue('xl', 'none');
  const pillBg = useColorModeValue('brand.50', 'whiteAlpha.100');
  const pillColor = useColorModeValue('brand.500', 'brand.200');

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        previousFocusRef.current = document.activeElement as HTMLElement;
        setOpen(true);
        setTimeout(() => posthog?.capture('palette_opened', { trigger: 'keyboard' }), 0);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setOpen, posthog]);

  // Reset state on close
  useEffect(() => {
    if (!open) {
      setQuery('');
      setSubCommand(null);
      setSubQuery('');
      previousFocusRef.current?.focus();
    }
  }, [open]);

  // Group routes by sidebar group
  const groupedRoutes = useMemo(() => {
    const groups = new Map<string, RType[]>();
    for (const route of visibleRoutes) {
      const group = route.group || 'command_palette.pages';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(route);
    }
    return groups;
  }, [visibleRoutes]);

  const handleSelectPage = useCallback(
    (route: RType) => {
      const fullPath = route.layout + route.path;
      navigate(fullPath);
      addRecentPage(fullPath, route.name);
      posthog?.capture('page_selected', { page: route.name, path: fullPath });
      setOpen(false);
    },
    [navigate, addRecentPage, posthog, setOpen],
  );

  const handleSelectAction = useCallback(
    (actionId: string) => {
      switch (actionId) {
        case 'toggle-dark-mode':
          toggleColorMode();
          break;
        case 'create-court':
          navigate('/admin/courts');
          break;
        case 'create-event':
          navigate('/admin/events');
          break;
      }
      posthog?.capture('action_executed', { action: actionId });
      setOpen(false);
    },
    [toggleColorMode, navigate, posthog, setOpen],
  );

  const handleSelectUser = useCallback(
    (user: UserSearchResult) => {
      navigate(`/admin/userDetails?userId=${user.id}`);
      posthog?.capture('user_searched', { userId: user.id });
      setOpen(false);
    },
    [navigate, posthog, setOpen],
  );

  const handleEnterSubCommand = useCallback(() => {
    if (!selectedOrganization) return;
    setSubCommand('users');
    setSubQuery('');
  }, [selectedOrganization]);

  const handleSubCommandKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && subQuery === '') {
        e.preventDefault();
        setSubCommand(null);
      }
    },
    [subQuery],
  );

  const actions = useMemo(
    () => [
      {
        id: 'toggle-dark-mode',
        name: colorMode === 'light'
          ? translate('command_palette.toggle_dark_mode')
          : translate('command_palette.toggle_light_mode'),
        icon: MdDarkMode,
      },
      // Create actions are capability-gated, not page-gated: a coach can VIEW
      // Terrains but the API rejects court mutations from non-admins, so
      // offering "create court" would only produce a 403 toast.
      ...(canManageOrgResources(role)
        ? [
            { id: 'create-court', name: translate('command_palette.create_court'), icon: MdOutlineAdd },
            { id: 'create-event', name: translate('command_palette.create_event'), icon: MdOutlineAdd },
          ]
        : []),
    ],
    [colorMode, role],
  );

  const hasQuery = query.length > 0;

  return (
    <AnimatePresence>
      {open && (
        <Box
          position="fixed"
          inset="0"
          zIndex="overlay"
          display="flex"
          justifyContent="center"
          onClick={() => setOpen(false)}
          sx={{
            '@media (prefers-reduced-motion: reduce)': {
              '& [data-palette-dialog]': { transform: 'none !important' },
            },
          }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: overlayBg,
              backdropFilter: 'blur(4px)',
            }}
          />

          {/* Dialog */}
          <motion.div
            data-palette-dialog
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '560px',
              marginTop: '20vh',
              height: 'fit-content',
              zIndex: 1,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              bg={dialogBg}
              border="1.5px solid"
              borderColor={borderColor}
              borderRadius="16px"
              boxShadow={shadow}
              overflow="hidden"
              w={{ base: '90vw', lg: '480px', xl: '560px' }}
              maxW="560px"
              mx="auto"
            >
              <Command
                label={translate('command_palette.aria_label' as any)}
                shouldFilter={subCommand === null}
              >
                {/* Input */}
                <Flex align="center" px="16px" h="48px" borderBottom="1px solid" borderColor={borderColor}>
                  {subCommand === 'users' ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.1 }}
                    >
                      <Flex
                        align="center"
                        bg={pillBg}
                        color={pillColor}
                        borderRadius="full"
                        px="8px"
                        py="1px"
                        mr="8px"
                        flexShrink={0}
                        {...typography.caption}
                      >
                        {translate('command_palette.users_pill' as any)} &rsaquo;
                      </Flex>
                    </motion.div>
                  ) : (
                    <Box as={MdSearch} w="16px" h="16px" color={textSecondary} mr="8px" flexShrink={0} />
                  )}
                  <Command.Input
                    ref={inputRef}
                    autoFocus
                    value={subCommand ? subQuery : query}
                    onValueChange={subCommand ? setSubQuery : setQuery}
                    onKeyDown={subCommand ? handleSubCommandKeyDown : undefined}
                    placeholder={
                      subCommand === 'users'
                        ? translate('command_palette.search_users_placeholder')
                        : translate('command_palette.search_placeholder')
                    }
                    style={{
                      flex: 1,
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontSize: 'var(--chakra-fontSizes-sm)',
                      fontWeight: 400,
                      color: 'inherit',
                    }}
                  />
                </Flex>

                {/* Results */}
                <Command.List
                  style={{
                    maxHeight: '320px',
                    overflowY: 'auto',
                    padding: '8px',
                  }}
                >
                  {subCommand === 'users' ? (
                    <UserSearchResults
                      results={userResults}
                      state={userSearchState}
                      isLoading={userSearchLoading}
                      isError={userSearchError}
                      hasQuery={hasUserQuery}
                      subQuery={subQuery}
                      textPrimary={textPrimary}
                      textSecondary={textSecondary}
                      itemActiveBg={itemActiveBg}
                      activeBorder={activeBorder}
                      onSelect={handleSelectUser}
                    />
                  ) : (
                    <>
                      <Command.Empty>
                        <Flex direction="column" align="center" py="40px">
                          <Box as={MdSearch} w="32px" h="32px" color={textSecondary} mb="8px" />
                          <Text {...typography.bodyBold} color={textPrimary}>
                            {translate('command_palette.no_results_title' as any)?.replace('{{query}}', query)}
                          </Text>
                          <Text {...typography.caption} color={textSecondary} mt="4px">
                            {translate('command_palette.no_results_hint' as any)}
                          </Text>
                        </Flex>
                      </Command.Empty>

                      {/* Recent pages */}
                      {!hasQuery && recentPages.length > 0 && (
                        <Command.Group heading={<GroupHeader>{translate('command_palette.recent' as any)}</GroupHeader>}>
                          {recentPages.map(page => (
                            <PaletteItem
                              key={page.path}
                              value={`recent-${page.name}`}
                              onSelect={() => {
                                const route = visibleRoutes.find(r => r.layout + r.path === page.path);
                                if (route) handleSelectPage(route);
                              }}
                              icon={null}
                              name={page.name}
                              hint={translate('command_palette.recent' as any)}
                              textPrimary={textPrimary}
                              textSecondary={textSecondary}
                              itemActiveBg={itemActiveBg}
                              activeBorder={activeBorder}
                            />
                          ))}
                        </Command.Group>
                      )}

                      {/* Pages grouped */}
                      {Array.from(groupedRoutes.entries()).map(([group, routes]) => (
                        <Command.Group key={group} heading={<GroupHeader>{translateGroupName(group)}</GroupHeader>}>
                          {routes.map(route => (
                            <PaletteItem
                              key={route.path}
                              value={route.name}
                              onSelect={() => handleSelectPage(route)}
                              icon={route.icon}
                              name={route.name}
                              hint={translateGroupName(group)}
                              textPrimary={textPrimary}
                              textSecondary={textSecondary}
                              itemActiveBg={itemActiveBg}
                              activeBorder={activeBorder}
                            />
                          ))}
                        </Command.Group>
                      ))}

                      {/* Actions */}
                      <Command.Group heading={<GroupHeader>{translate('command_palette.actions' as any)}</GroupHeader>}>
                        {actions.map(action => (
                          <PaletteItem
                            key={action.id}
                            value={action.name}
                            onSelect={() => handleSelectAction(action.id)}
                            icon={<Box as={action.icon} w="20px" h="20px" color={textSecondary} />}
                            name={action.name}
                            textPrimary={textPrimary}
                            textSecondary={textSecondary}
                            itemActiveBg={itemActiveBg}
                            activeBorder={activeBorder}
                          />
                        ))}
                      </Command.Group>

                      {/* Search users sub-command entry — lands on /admin/userDetails */}
                      {canSearchUsers && (
                        <Command.Group heading={<GroupHeader>{translate('command_palette.search' as any)}</GroupHeader>}>
                          <Tooltip
                            label={!selectedOrganization ? translate('command_palette.select_org_first' as any) : undefined}
                            placement="top"
                            hasArrow
                            isDisabled={!!selectedOrganization}
                          >
                            <Box>
                              <PaletteItem
                                value={translate('command_palette.search_users' as any)}
                                onSelect={handleEnterSubCommand}
                                icon={<Box as={MdPerson} w="20px" h="20px" color={textSecondary} />}
                                name={translate('command_palette.search_users' as any)}
                                textPrimary={selectedOrganization ? textPrimary : textSecondary}
                                textSecondary={textSecondary}
                                itemActiveBg={selectedOrganization ? itemActiveBg : 'transparent'}
                                activeBorder={activeBorder}
                                disabled={!selectedOrganization}
                              />
                            </Box>
                          </Tooltip>
                        </Command.Group>
                      )}
                    </>
                  )}
                </Command.List>

                {/* Keyboard hint bar */}
                <Flex
                  justify="center"
                  align="center"
                  h="32px"
                  borderTop="1px solid"
                  borderColor={borderColor}
                  bg={pageBg}
                  gap="16px"
                >
                  <Text {...typography.caption} color={textSecondary}>
                    {subCommand
                      ? translate('command_palette.keyboard_hint_sub' as any)
                      : translate('command_palette.keyboard_hint' as any)}
                  </Text>
                </Flex>
              </Command>
            </Box>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function GroupHeader({ children }: { children: React.ReactNode }) {
  return (
    <Text
      {...typography.caption}
      color="secondaryGray.600"
      textTransform="uppercase"
      letterSpacing="0.06em"
      px="16px"
      pt="8px"
      pb="4px"
    >
      {children}
    </Text>
  );
}

interface PaletteItemProps {
  value: string;
  onSelect: () => void;
  icon: React.ReactNode;
  name: string;
  hint?: string;
  textPrimary: string;
  textSecondary: string;
  itemActiveBg: string;
  activeBorder: string;
  disabled?: boolean;
}

function PaletteItem({
  value,
  onSelect,
  icon,
  name,
  hint,
  textPrimary,
  textSecondary,
  itemActiveBg,
  activeBorder,
  disabled,
}: PaletteItemProps) {
  return (
    <Command.Item
      value={value}
      onSelect={disabled ? undefined : onSelect}
      disabled={disabled}
      style={{ borderRadius: '10px' }}
    >
      <Flex
        align="center"
        h="40px"
        px="16px"
        borderRadius="md"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        opacity={disabled ? 0.4 : 1}
        _hover={!disabled ? { bg: itemActiveBg } : undefined}
        sx={{
          '[data-selected=true] &': {
            bg: itemActiveBg,
            borderLeft: `2px solid`,
            borderColor: activeBorder,
          },
        }}
      >
        {icon && (
          <Box flexShrink={0} mr="12px" display="flex" alignItems="center">
            {icon}
          </Box>
        )}
        <Text {...typography.bodyBold} color={textPrimary} flex={1} isTruncated>
          {name}
        </Text>
        {hint && (
          <Text {...typography.caption} color={textSecondary} ml="8px" flexShrink={0}>
            {hint}
          </Text>
        )}
      </Flex>
    </Command.Item>
  );
}

interface UserSearchResultsProps {
  results: UserSearchResult[];
  state: ReadState;
  isLoading: boolean;
  isError: boolean;
  hasQuery: boolean;
  subQuery: string;
  textPrimary: string;
  textSecondary: string;
  itemActiveBg: string;
  activeBorder: string;
  onSelect: (user: UserSearchResult) => void;
}

function UserSearchResults({
  results,
  state,
  isLoading,
  isError,
  hasQuery,
  subQuery,
  textPrimary,
  textSecondary,
  itemActiveBg,
  activeBorder,
  onSelect,
}: UserSearchResultsProps) {
  if (!hasQuery) {
    return (
      <Flex direction="column" align="center" py="40px">
        <Box as={MdPerson} w="32px" h="32px" color={textSecondary} mb="8px" />
        <Text {...typography.caption} color={textSecondary}>
          {translate('command_palette.min_chars_hint' as any)}
        </Text>
      </Flex>
    );
  }

  // A read that was never issued is not a search that found nothing. Today the
  // only way in here is a palette item disabled without an organization, so
  // this branch is defence in depth rather than a live path — but the branch
  // BELOW it says "No users found for '{{query}}'", and that sentence must
  // never be reachable from a query nobody sent.
  if (state === 'never-issued') {
    return (
      <Flex direction="column" align="center" py="40px" data-testid="user-search-never-issued">
        <Box as={MdPerson} w="32px" h="32px" color={textSecondary} mb="8px" />
        <Text {...typography.caption} color={textSecondary}>
          {translate(scopePromptI18nKey('organization') as any)}
        </Text>
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex direction="column" align="center" py="40px">
        <Spinner size="sm" color="brand.500" mb="8px" />
        <Text {...typography.caption} color={textSecondary}>
          {translate('command_palette.searching' as any)}
        </Text>
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex direction="column" align="center" py="40px">
        <Text {...typography.caption} color="red.500">
          {translate('command_palette.search_failed' as any)}
        </Text>
      </Flex>
    );
  }

  if (results.length === 0) {
    return (
      <Flex direction="column" align="center" py="40px">
        <Box as={MdPerson} w="32px" h="32px" color={textSecondary} mb="8px" />
        <Text {...typography.bodyBold} color={textPrimary}>
          {translate('command_palette.no_users_found' as any)?.replace('{{query}}', subQuery)}
        </Text>
      </Flex>
    );
  }

  return (
    <>
      {results.map(user => (
        <Command.Item
          key={user.id}
          value={`${user.name} ${user.email}`}
          onSelect={() => onSelect(user)}
          style={{ borderRadius: '10px' }}
        >
          <Flex
            align="center"
            h="40px"
            px="16px"
            borderRadius="md"
            cursor="pointer"
            _hover={{ bg: itemActiveBg }}
            sx={{
              '[data-selected=true] &': {
                bg: itemActiveBg,
                borderLeft: `2px solid`,
                borderColor: activeBorder,
              },
            }}
          >
            <Avatar
              name={user.name}
              src={user.avatarUrl}
              size="xs"
              w="24px"
              h="24px"
              mr="12px"
            />
            <Text {...typography.bodyBold} color={textPrimary} flex={1} isTruncated>
              {user.name}
            </Text>
            <Text {...typography.caption} color={textSecondary} ml="8px" isTruncated maxW="200px">
              {user.email}
            </Text>
          </Flex>
        </Command.Item>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function translateGroupName(group: string): string {
  // Group keys like 'sidebar.groups.organization' are translation keys
  const translated = translate(group as any);
  if (translated && translated !== group) return translated;
  // Fallback for ungrouped routes (e.g., 'Pages')
  const parts = group.split('.');
  const last = parts[parts.length - 1];
  return last.charAt(0).toUpperCase() + last.slice(1);
}
